/**
 * @module app.core
 */

import { autoinject, transient } from 'aurelia-framework';
import { Api } from 'app/shared/lib/essette-api';
import { IApiActionResults, IQueryLoadOptions, IApiQueryResults, IApiBatchProgress } from 'app/shared/lib/api/api-types';
import { ITasksConfig, IBaseConfig } from 'app/core/models/essette-interfaces';
import { DateValue } from 'app/shared/lib/date-time';
import { Toaster } from 'app/shared/lib/toaster';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@transient()
@autoinject()
export class TaskService {

    ////****************** Fields and Properties ************************************////
    public isConfigured: boolean = false;
    public config: ITasksConfig = {};
    private correspondenceConfig: IBaseConfig = {
        serviceRootUrl: 'cm/cases',
    }
    private taskType: 'tasks' | 'interventions' = 'tasks';


    ////****************** Functions ************************************************////
    constructor(
        private readonly api: Api,
        private readonly logger: AppLogger,
        private readonly toaster: Toaster) {

        this.logger.source = this;
    }

    public configure(config: ITasksConfig): void {
        this.config = config;
        switch(this.config.primaryTargetType) {
            case 'CM.CarePlanIntervention': this.taskType = 'interventions';
                break;
            default:
                this.taskType = "tasks";
                break;
        };
        this.isConfigured = true;
    }

    private checkConfiguration(): void {
        if(!this.isConfigured) {
            throw Error("Task service requires module-specific configuration! Use configure() method.");
        }
    }

    ////****************** Queries **************************************************////
    public async search(searchFilters: ITaskSearchFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.Task> | null> {
        if(!searchFilters) { return null; }
        //TODO: Add filter count and log/toaster warnings (see case/auth/member searches)

        this.checkConfiguration();

        const query = this.api
            .query(`${this.config.serviceRootUrl}/${this.taskType}`)
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(searchFilters) {
            if(searchFilters.dueDateStart) {
                // 'due.date' isn't a full timestamp so it behaves differently when filtering like this
                // so we have to subtract a day from each end to be inclusive
                query.filterDateTimeBetweenDates(
                    'due.date',
                    new DateValue(searchFilters.dueDateStart).add(-1, 'days'),
                    searchFilters.dueDateEnd ? new DateValue(searchFilters.dueDateEnd).add(-1, 'days') : null);
            }
            if(searchFilters.type?.code) {
                query.filterString('attachedTo.type.code', searchFilters.type.code);
            }
            if(searchFilters.caseNumber) {
                query.filterString('case.number', searchFilters.caseNumber, 'startsWith');
            }
            if(searchFilters.memberId) {
                query.filterString('member.identifier.primary', searchFilters.memberId, 'startsWith');
            }
            if(searchFilters.memberMedicaidId) {
                query.filterString('member.identifier.medicaid', searchFilters.memberMedicaidId, 'startsWith');
            }
            if(searchFilters.memberFirstName) {
                query.filterString('member.name.first', searchFilters.memberFirstName, 'startsWith');
            }
            if(searchFilters.memberLastName) {
                query.filterString('member.name.last', searchFilters.memberLastName, 'startsWith');
            }
            if(searchFilters.queue?.id) {
                query.filterIdentifier('delegated.queue.id', searchFilters.queue.id);
            }

            let myFilterCount = 0;
            if(searchFilters.myCases) myFilterCount++;
            if(searchFilters.delegatedToMe) myFilterCount++;
            if(searchFilters.myCareTeam) myFilterCount++;
            if(myFilterCount <= 1) {
                // if 0-1 "My" filters selected, run each filter individually
                if(searchFilters.assignedTo?.id) {
                    query.filterIdentifier('case.assignedTo.id', searchFilters.assignedTo.id);
                }
                if(searchFilters.delegatedToProvider?.id) {
                    query.filterIdentifier('delegated.toProvider.id', searchFilters.delegatedToProvider.id);
                }
                if(searchFilters.delegatedToUser?.id) {
                    query.filterIdentifier('delegated.toUser.id', searchFilters.delegatedToUser.id);
                }
                if(searchFilters.careTeamParticipant?.id) {
                    query.filterExists('case.careteam.participants', f =>
                        f.filterIdentifier('assigned.toUser.id', searchFilters.careTeamParticipant?.id!)
                    );
                }
            } else {
                // if more than one "My" filter is selected, construct the filterOr on the query
                query.filterOr((or) => {
                    if(searchFilters.myCases && searchFilters.assignedTo?.id) {
                        or.filterIdentifier('case.assignedTo.id', searchFilters.assignedTo.id);
                    }
                    if(searchFilters.delegatedToMe && searchFilters.delegatedToUser?.id) {
                        or.filterIdentifier('delegated.toUser.id', searchFilters.delegatedToUser.id);
                    }
                    if(searchFilters.myCareTeam && searchFilters.careTeamParticipant?.id) {
                        or.filterExists('case.careteam.participants', (f =>
                            f.filterIdentifier('assigned.toUser.id', searchFilters.careTeamParticipant?.id!)
                        ));
                    }
                });
                // and then filter based on any additional "non-My" fields that may be selected
                if(!searchFilters.myCases && searchFilters.assignedTo?.id) {
                    query.filterIdentifier('case.assignedTo.id', searchFilters.assignedTo.id);
                }
                if(!searchFilters.delegatedToMe && searchFilters.delegatedToProvider?.id) {
                    query.filterIdentifier('delegated.toProvider.id', searchFilters.delegatedToProvider.id)
                }
                if(!searchFilters.delegatedToMe && searchFilters.delegatedToUser?.id) {
                    query.filterIdentifier('delegated.toUser.id', searchFilters.delegatedToUser.id)
                }
                if(!searchFilters.myCareTeam && searchFilters.careTeamParticipant?.id) {
                    query.filterExists('case.careteam.participants', (f =>
                        f.filterIdentifier('assigned.toUser.id', searchFilters.careTeamParticipant?.id!)
                    ));
                }
            }

            if(searchFilters.delegatedBy?.id) {
                query.filterIdentifier('metadata.delegated.byUser.id', searchFilters.delegatedBy.id);
            }
            if(searchFilters.overdue) {
                query.filterDateSpecial('due.date', 'today', 'lt');
            }
            if(searchFilters.eligibility) {
                query.filterBoolean('member.eligibility.isCurrent', true);
            }
            if(searchFilters.description) {
                query.filterString('description', searchFilters.description, 'contains');
            }
            if(searchFilters.isOpen) {
                query.filterBoolean('isCompleted', false);
                query.filterBoolean('case.isClosed', false);
            }

        }

        return await this.api.get(query);
    }

    public async load(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.Task> | null> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/${this.taskType}`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterBoolean('isCompleted', false)
            .filterBoolean('case.isClosed', false);

        return await this.api.get(query);
    }

    public async loadTasksByTarget(targetId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeComplete: boolean = true, fields: string[] = []): Promise<IApiQueryResults<Core.Task>> {
        this.checkConfiguration();
        // prioritize using rollup config's service root URL if it exists
        const serviceRootUrl = this.config.rollupConfig?.serviceRootUrl || this.config.serviceRootUrl;
        let loadUrl: string = this.taskType === 'tasks' ? serviceRootUrl! : `${this.config.serviceRootUrl}/goals`;
        const query = this.api
            .query(`${loadUrl}/${targetId}/${this.taskType}`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(!includeComplete) {
            query.filterBoolean('isCompleted', false);
        }

        return await this.api.get(query);
    }

    public async searchDefinitions(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.TaskDefinition>> {
        const query = this.api
            .query(`${this.config.serviceRootUrl}/${this.taskType}/definitions`)
            .strategy('search')
            .filterString('description', searchValue, { operator: 'contains' })
            .applyStandardLoadOptions(options || null)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadTaskDefinitions(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.TaskDefinition>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/${this.taskType}/definitions`)
            .filterBoolean('isCustom', false)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadAppointmentsByTask(taskId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.TaskAppointment>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/${this.taskType}/${taskId}/appointments`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadNotesByTask(taskId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.TaskNote>> {
        const query = this.api
            .query(`${this.config.serviceRootUrl}/${this.taskType}/${taskId}/notes`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadRecommendedInterventions(definitionId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.TaskDefinition>> {
        const query = this.api
            .query(`${this.config.serviceRootUrl}/goals/definitions/${definitionId}/suggestions/interventions`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    public async loadTask(taskId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.Task>> {
        this.checkConfiguration();

        const query = this.api
            .query(`${this.config.serviceRootUrl}/${this.taskType}/${taskId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadTaskDefinition(taskDefinitionId: string, includes: string[] = [], taskType?: string): Promise<IApiQueryResults<Core.Task>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.taskServiceRootUrl ?? this.config.serviceRootUrl}/${taskType ?? this.taskType}/definitions/${taskDefinitionId}`)
            .include(...includes);

        return await this.api.get(query);
    }


    ////****************** Actions **************************************************////
    public async insertTaskDefinition(taskDefinition: Core.TaskDefinition): Promise<IApiActionResults> {
        this.checkConfiguration();

        const action = this.api.action('insert', `${this.config.serviceRootUrl}/${this.taskType}/definitions`, taskDefinition);
        return await this.api.post(action);
    }

    public async insertTask(task: Core.Task, addToLibrary: boolean = false, taskType?: string): Promise<IApiActionResults> {
        this.checkConfiguration();

        const action = this.api.action('insert', `${this.config.taskServiceRootUrl ?? this.config.serviceRootUrl}/${taskType ?? this.taskType}`, task);
        return await this.api.post(action);
    }

    public async updateTask(taskId: string, task: Core.Task): Promise<IApiActionResults> {
        this.checkConfiguration();

        const action = this.api.action('update', `${this.config.serviceRootUrl}/${this.taskType}/${taskId}`, task);
        return await this.api.post(action);
    }

    public async updateTasksBatch(tasks: Core.Task[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        this.checkConfiguration();

        const batch = this.api.batch();
        for(const task of tasks) {
            batch.post(this.api.action('update', `${this.config.serviceRootUrl}/${this.taskType}/${task.id}`, task));
        }
        return await this.api.execute(batch, { progress });
    }

    public async completeTask(taskId: string): Promise<IApiActionResults> {
        this.checkConfiguration();

        const action = this.api.action('complete', `${this.config.serviceRootUrl}/${this.taskType}/${taskId}`, { isCompleted: true });
        return await this.api.post(action);
    }

    public async activateTask(taskId: string, isActive: boolean): Promise<IApiActionResults> {
        this.checkConfiguration();

        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `${this.config.serviceRootUrl}/${this.taskType}/${taskId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveTaskAppointment(appointment: Core.TaskAppointment): Promise<IApiActionResults> {
        this.checkConfiguration();

        const actionName = !!appointment.id ? 'update' : 'insert';
        const actionUrl = `${this.config.serviceRootUrl}/${this.taskType}/appointments` + (appointment.id ? `/${appointment.id}` : '');

        const action = this.api.action(actionName, actionUrl, appointment);
        return await this.api.post(action);
    }

    public async activateTaskAppointment(taskAppointmentId: string, isActive: boolean): Promise<IApiActionResults> {
        this.checkConfiguration();

        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `${this.config.serviceRootUrl}/${this.taskType}/appointments/${taskAppointmentId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    //created based on correspondencesService.insertLetter
    public async insertSmartTaskLetter(letter: Core.CorrespondenceLetter): Promise<IApiActionResults> {
        this.checkConfiguration();

        const action = this.api.action("insert", `${this.correspondenceConfig.serviceRootUrl}/correspondence/letters`, letter);
        return await this.api.post(action);
    }

    public async insertSmartTaskLettersBatch(letters: Core.CorrespondenceLetter[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        this.checkConfiguration();
        const batch = this.api.batch();
        for(const l of letters) {
            batch.post(this.api.action('insert', `${this.correspondenceConfig.serviceRootUrl}/correspondence/letters`, l));
        }
        return await this.api.execute(batch, { progress });
    }
}

export interface ITaskSearchFilters {
    dueDateStart?: string | null;
    dueDateEnd?: string | null;
    type?: Core.TaskTargetType | null;
    caseNumber?: string | null;
    memberId?: string | null;
    memberMedicaidId?: string | null;
    memberFirstName?: string | null;
    memberLastName?: string | null;
    queue?: Core.Queue | null;
    assignedTo?: Core.User | null;
    delegatedToProvider?: CD.Provider | null;
    delegatedToUser?: Core.User | null;
    delegatedBy?: Core.User | null;
    overdue?: boolean | null;
    eligibility?: boolean | null;
    myCases?: boolean | null;
    myCareTeam?: boolean | null;
    delegatedToMe?: boolean | null;
    careTeamParticipant?: Core.User | null;
    description?: string | null;
    isOpen?: boolean | null;
}
